/**
 * One game's chat thread. Any group member can post; @ClubBot invokes the full
 * agent (acting as the mentioning member). Polls while open. Decoupled from
 * SessionDTO (takes sessionId + a title) so the club feed can open it too.
 */
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import {
  getSessionMessages,
  postSessionMessage,
  type SessionMessageDTO,
} from "../api";

const POLL_MS = 7000;

export function SessionChatModal({
  sessionId,
  title,
  meId,
  onClose,
}: {
  sessionId: string;
  title: string;
  meId: string;
  onClose: () => void;
}) {
  const [messages, setMessages] = useState<SessionMessageDTO[]>([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const lastTsRef = useRef<string | undefined>(undefined);

  function merge(incoming: SessionMessageDTO[]) {
    if (incoming.length === 0) return;
    setMessages((prev) => {
      const seen = new Set(prev.map((m) => m.id));
      const next = [...prev, ...incoming.filter((m) => !seen.has(m.id))];
      lastTsRef.current = next[next.length - 1]?.created_at;
      return next;
    });
  }

  useEffect(() => {
    let cancelled = false;
    getSessionMessages(sessionId)
      .then((m) => {
        if (cancelled) return;
        setMessages(m);
        lastTsRef.current = m[m.length - 1]?.created_at;
      })
      .catch((e) => !cancelled && setError(String(e?.message ?? e)));
    return () => { cancelled = true; };
  }, [sessionId]);

  useEffect(() => {
    const t = setInterval(() => {
      getSessionMessages(sessionId, lastTsRef.current).then(merge).catch(() => {});
    }, POLL_MS);
    return () => clearInterval(t);
  }, [sessionId]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages]);

  async function send() {
    const body = input.trim();
    if (!body || sending) return;
    setSending(true);
    setError(null);
    try {
      const posted = await postSessionMessage(sessionId, body);
      setInput("");
      merge(posted);
    } catch (e) {
      setError(String((e as Error).message));
    } finally {
      setSending(false);
    }
  }

  return createPortal(
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-ink/55 cs-modal-overlay" onClick={onClose} />
      <div className="absolute bottom-0 left-0 right-0 cs-modal-card sm:inset-0 sm:bottom-auto sm:top-1/2 sm:-translate-y-1/2 sm:max-w-md sm:mx-auto sm:my-0">
        <div className="max-w-md mx-auto px-4 pb-4 sm:pt-0" style={{ paddingBottom: "max(1rem, env(safe-area-inset-bottom))" }}>
          <div className="bg-bg border-2 border-ink rounded-t-xl sm:rounded-xl shadow-2xl overflow-hidden flex flex-col">
            <div className="px-5 py-3 bg-ink text-bg flex items-center justify-between shrink-0">
              <div className="font-mono text-[11px] uppercase tracking-[0.16em] truncate">
                Game chat · {title}
              </div>
              <button
                onClick={onClose}
                className="text-bg/60 hover:text-bg font-mono text-2xl leading-none ml-3"
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div ref={scrollRef} className="p-4 h-[55vh] overflow-y-auto flex flex-col gap-3">
              {messages.length === 0 && !error && (
                <div className="text-sm text-ink/45 text-center mt-6">
                  No messages yet. Say hi — or ask <b>@ClubBot</b> who's in.
                </div>
              )}
              {messages.map((m) => (
                <Bubble key={m.id} m={m} mine={m.user_id === meId} />
              ))}
            </div>

            {error && <div className="px-4 pb-1 text-sm text-[#C84B31]">{error}</div>}

            <div className="p-3 border-t border-ink/10 flex items-end gap-2 shrink-0">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    void send();
                  }
                }}
                rows={1}
                placeholder="Message the group… (@ClubBot to ask the bot)"
                className="flex-1 resize-none rounded-md border border-ink/20 px-3 py-2 text-sm focus:outline-none focus:border-ink/50 bg-bg"
              />
              <button
                onClick={() => void send()}
                disabled={sending || !input.trim()}
                className="px-4 py-2 bg-ink text-bg font-display font-bold text-sm cs-tap rounded-md disabled:opacity-40"
              >
                {sending ? "…" : "Send"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function Bubble({ m, mine }: { m: SessionMessageDTO; mine: boolean }) {
  if (m.is_bot) {
    return (
      <div className="self-start max-w-[88%]">
        <div className="font-mono text-[9px] uppercase tracking-[0.16em] text-accent-dim mb-1">
          ClubBot
        </div>
        <div className="rounded-2xl rounded-tl-sm border-2 border-accent-dim bg-bg px-3.5 py-2 text-sm whitespace-pre-wrap">
          {m.body}
        </div>
      </div>
    );
  }
  if (mine) {
    return (
      <div className="self-end max-w-[85%]">
        <div className="rounded-2xl rounded-br-sm bg-ink text-bg px-3.5 py-2 text-sm whitespace-pre-wrap">
          {m.body}
        </div>
      </div>
    );
  }
  return (
    <div className="self-start max-w-[85%]">
      <div className="font-mono text-[9px] uppercase tracking-[0.14em] text-ink/45 mb-1">
        {m.name}
      </div>
      <div className="rounded-2xl rounded-tl-sm border border-ink/15 bg-bg px-3.5 py-2 text-sm whitespace-pre-wrap">
        {m.body}
      </div>
    </div>
  );
}
